import React, { useMemo, useState } from 'react';
import {
  Users,
  AlertTriangle,
  CheckCircle,
  Clock,
  Building2,
  UserCheck,
  GitMerge,
  ChevronDown,
  ChevronUp,
  Target,
  Layers,
  Shield,
  TrendingUp
} from 'lucide-react';
import { OptimizationOpportunity } from '../../services/processOptimizationAPI';

interface StakeholderAggregate {
  name: string;
  department: string;
  role_type: string;
  opportunities_involved: number[];
  approval_required_count: number;
  is_bottleneck: boolean;
  total_effort_hours: number;
}

interface BatchingSuggestion {
  approver: string;
  opportunities: { id: number; title: string }[];
  reason: string;
}

interface StakeholderSummaryProps {
  opportunities: OptimizationOpportunity[];
}

const StakeholderSummary: React.FC<StakeholderSummaryProps> = ({ opportunities }) => {
  const [isExpanded, setIsExpanded] = useState(true);

  const aggregatedData = useMemo(() => {
    const stakeholderMap = new Map<string, StakeholderAggregate>();
    const departmentSet = new Set<string>();
    let totalApprovers = 0;
    let totalCriticalRisks = 0;
    let totalHighRisks = 0;

    opportunities.forEach(opp => {
      const affectedSteps = opp.affected_steps || [];
      const integrationScore = opp.integration_complexity_score || 5;
      const regulatoryScore = opp.regulatory_risk_score || 5;
      const governanceScore = opp.governance_complexity_score || 5;

      affectedSteps.forEach((step: any) => {
        if (step.department) departmentSet.add(step.department);
      });

      const processOwnerKey = 'Process Owner';
      if (!stakeholderMap.has(processOwnerKey)) {
        stakeholderMap.set(processOwnerKey, {
          name: processOwnerKey,
          department: 'Operations',
          role_type: 'process_owner',
          opportunities_involved: [],
          approval_required_count: 0,
          is_bottleneck: false,
          total_effort_hours: 0
        });
      }
      const processOwner = stakeholderMap.get(processOwnerKey)!;
      processOwner.opportunities_involved.push(opp.id);
      processOwner.approval_required_count++;
      processOwner.total_effort_hours += 8;

      if (integrationScore >= 5) {
        const itKey = 'IT Director';
        if (!stakeholderMap.has(itKey)) {
          stakeholderMap.set(itKey, {
            name: itKey,
            department: 'Information Technology',
            role_type: 'it',
            opportunities_involved: [],
            approval_required_count: 0,
            is_bottleneck: false,
            total_effort_hours: 0
          });
        }
        const itStakeholder = stakeholderMap.get(itKey)!;
        itStakeholder.opportunities_involved.push(opp.id);
        if (integrationScore >= 7) {
          itStakeholder.approval_required_count++;
          itStakeholder.total_effort_hours += 16;
        } else {
          itStakeholder.total_effort_hours += 4;
        }
      }

      if (regulatoryScore >= 5) {
        const complianceKey = 'Compliance Officer';
        if (!stakeholderMap.has(complianceKey)) {
          stakeholderMap.set(complianceKey, {
            name: complianceKey,
            department: 'Compliance',
            role_type: 'compliance',
            opportunities_involved: [],
            approval_required_count: 0,
            is_bottleneck: false,
            total_effort_hours: 0
          });
        }
        const complianceStakeholder = stakeholderMap.get(complianceKey)!;
        complianceStakeholder.opportunities_involved.push(opp.id);
        if (regulatoryScore >= 7) {
          complianceStakeholder.approval_required_count++;
          complianceStakeholder.total_effort_hours += 12;
        } else {
          complianceStakeholder.total_effort_hours += 4;
        }
      }

      if (governanceScore >= 7) {
        const execKey = 'COO / Operations Executive';
        if (!stakeholderMap.has(execKey)) {
          stakeholderMap.set(execKey, {
            name: execKey,
            department: 'Executive',
            role_type: 'executive',
            opportunities_involved: [],
            approval_required_count: 0,
            is_bottleneck: false,
            total_effort_hours: 0
          });
        }
        const execStakeholder = stakeholderMap.get(execKey)!;
        execStakeholder.opportunities_involved.push(opp.id);
        execStakeholder.approval_required_count++;
        execStakeholder.total_effort_hours += 2;
      }

      const isFullAutomation = opp.inefficiency_type === 'manual_pattern' && affectedSteps.length >= 3;
      if (isFullAutomation) {
        totalCriticalRisks++;
      }

      const handoffCount = affectedSteps.filter((step: any) =>
        step.action_type === 'handoff' ||
        step.name?.toLowerCase().includes('handoff')
      ).length;
      if (handoffCount >= 2) {
        totalHighRisks++;
      }
    });

    stakeholderMap.forEach(stakeholder => {
      if (stakeholder.approval_required_count >= 3) {
        stakeholder.is_bottleneck = true;
      }
    });

    const stakeholders = Array.from(stakeholderMap.values())
      .sort((a, b) => b.opportunities_involved.length - a.opportunities_involved.length);

    const bottleneckStakeholders = stakeholders.filter(s => s.is_bottleneck);

    const batchingSuggestions: BatchingSuggestion[] = [];
    bottleneckStakeholders.forEach(stakeholder => {
      if (stakeholder.opportunities_involved.length >= 3) {
        const involvedOpps = opportunities
          .filter(o => stakeholder.opportunities_involved.includes(o.id))
          .slice(0, 5);

        batchingSuggestions.push({
          approver: stakeholder.name,
          opportunities: involvedOpps.map(o => ({ id: o.id, title: o.title })),
          reason: `${stakeholder.name} is involved in ${stakeholder.opportunities_involved.length} opportunities. Batching approvals can reduce review cycles.`
        });
      }
    });

    return {
      stakeholders,
      bottleneckStakeholders,
      batchingSuggestions,
      totalDepartments: departmentSet.size,
      totalStakeholders: stakeholders.length,
      totalApprovals: stakeholders.reduce((sum, s) => sum + s.approval_required_count, 0),
      totalEffortHours: stakeholders.reduce((sum, s) => sum + s.total_effort_hours, 0),
      totalCriticalRisks,
      totalHighRisks
    };
  }, [opportunities]);

  const getRoleIcon = (roleType: string) => {
    switch (roleType) {
      case 'process_owner': return '👤';
      case 'it': return '💻';
      case 'compliance': return '🛡️';
      case 'executive': return '🏢';
      default: return '👥';
    }
  };

  return (
    <div className="bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 rounded-xl border-2 border-indigo-200 shadow-lg">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full px-6 py-4 flex items-center justify-between hover:bg-indigo-100/30 transition-colors rounded-t-xl"
      >
        <div className="flex items-center gap-3">
          <div className="p-2 bg-indigo-100 rounded-lg">
            <Users className="w-6 h-6 text-indigo-600" />
          </div>
          <div className="text-left">
            <h3 className="text-lg font-bold text-indigo-900">Stakeholder & Governance Summary</h3>
            <p className="text-sm text-indigo-600">Cross-opportunity stakeholder analysis and approval requirements</p>
          </div>
        </div>
        <div className="flex items-center gap-4">
          {aggregatedData.bottleneckStakeholders.length > 0 && (
            <span className="px-3 py-1 bg-red-100 text-red-800 text-sm font-bold rounded-lg border border-red-300 flex items-center gap-1">
              <AlertTriangle className="w-4 h-4" />
              {aggregatedData.bottleneckStakeholders.length} Bottleneck{aggregatedData.bottleneckStakeholders.length > 1 ? 's' : ''}
            </span>
          )}
          {isExpanded ? (
            <ChevronUp className="w-6 h-6 text-indigo-600" />
          ) : (
            <ChevronDown className="w-6 h-6 text-indigo-600" />
          )}
        </div>
      </button>

      {isExpanded && (
        <div className="px-6 pb-6 space-y-6">
          <div className="grid grid-cols-5 gap-4">
            <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm text-center">
              <div className="text-3xl font-bold text-indigo-600">{aggregatedData.totalStakeholders}</div>
              <div className="text-sm text-gray-600 mt-1">Key Stakeholders</div>
            </div>
            <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm text-center">
              <div className="text-3xl font-bold text-purple-600">{aggregatedData.totalApprovals}</div>
              <div className="text-sm text-gray-600 mt-1">Total Approvals</div>
            </div>
            <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm text-center">
              <div className="text-3xl font-bold text-teal-600">{aggregatedData.totalDepartments}</div>
              <div className="text-sm text-gray-600 mt-1">Departments</div>
            </div>
            <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm text-center">
              <div className="text-3xl font-bold text-amber-600">{aggregatedData.totalEffortHours}h</div>
              <div className="text-sm text-gray-600 mt-1">Est. Effort</div>
            </div>
            <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm text-center">
              <div className={`text-3xl font-bold ${
                aggregatedData.totalCriticalRisks > 0 ? 'text-red-600' :
                aggregatedData.totalHighRisks > 0 ? 'text-orange-600' : 'text-green-600'
              }`}>
                {aggregatedData.totalCriticalRisks + aggregatedData.totalHighRisks}
              </div>
              <div className="text-sm text-gray-600 mt-1">Resistance Risks</div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-6">
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
              <div className="px-4 py-3 bg-gray-50 border-b border-gray-200 rounded-t-xl">
                <h4 className="font-semibold text-gray-800 flex items-center gap-2">
                  <UserCheck className="w-5 h-5 text-indigo-600" />
                  Key Stakeholders Across All Opportunities
                </h4>
              </div>
              <div className="p-4 space-y-3 max-h-80 overflow-y-auto">
                {aggregatedData.stakeholders.map((stakeholder, idx) => (
                  <div
                    key={stakeholder.name}
                    className={`flex items-center justify-between p-3 rounded-lg ${
                      stakeholder.is_bottleneck ? 'bg-red-50 border border-red-200' : 'bg-gray-50'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-xl">{getRoleIcon(stakeholder.role_type)}</span>
                      <div>
                        <div className="font-medium text-gray-900 flex items-center gap-2">
                          {stakeholder.name}
                          {stakeholder.is_bottleneck && (
                            <span className="px-1.5 py-0.5 bg-red-100 text-red-700 text-xs font-bold rounded">
                              BOTTLENECK
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-gray-500">{stakeholder.department}</div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm font-bold text-indigo-600">
                        {stakeholder.opportunities_involved.length} opportunities
                      </div>
                      <div className="text-xs text-gray-500">
                        {stakeholder.approval_required_count} approvals required
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="space-y-4">
              {aggregatedData.bottleneckStakeholders.length > 0 && (
                <div className="bg-red-50 rounded-xl border border-red-200 p-4">
                  <h4 className="font-semibold text-red-800 flex items-center gap-2 mb-3">
                    <AlertTriangle className="w-5 h-5" />
                    Approval Bottlenecks Identified
                  </h4>
                  <div className="space-y-2">
                    {aggregatedData.bottleneckStakeholders.map(stakeholder => (
                      <div key={stakeholder.name} className="flex items-center justify-between p-2 bg-white rounded-lg">
                        <span className="font-medium text-gray-800">{stakeholder.name}</span>
                        <span className="text-sm text-red-600 font-bold">
                          Blocks {stakeholder.approval_required_count} opportunities
                        </span>
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-red-700 mt-3">
                    These stakeholders are involved in multiple approvals. Consider parallel processing or delegation.
                  </p>
                </div>
              )}

              {aggregatedData.batchingSuggestions.length > 0 && (
                <div className="bg-amber-50 rounded-xl border border-amber-200 p-4">
                  <h4 className="font-semibold text-amber-800 flex items-center gap-2 mb-3">
                    <Layers className="w-5 h-5" />
                    Batching Recommendations
                  </h4>
                  <div className="space-y-3">
                    {aggregatedData.batchingSuggestions.slice(0, 3).map((suggestion, idx) => (
                      <div key={idx} className="p-3 bg-white rounded-lg border border-amber-100">
                        <div className="font-medium text-gray-800 mb-1">
                          Get these approved together by {suggestion.approver}:
                        </div>
                        <div className="flex flex-wrap gap-1">
                          {suggestion.opportunities.map(opp => (
                            <span key={opp.id} className="px-2 py-0.5 bg-amber-100 text-amber-800 text-xs rounded">
                              #{opp.id}
                            </span>
                          ))}
                        </div>
                        <p className="text-xs text-gray-600 mt-2">{suggestion.reason}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {aggregatedData.totalCriticalRisks > 0 || aggregatedData.totalHighRisks > 0 ? (
                <div className="bg-orange-50 rounded-xl border border-orange-200 p-4">
                  <h4 className="font-semibold text-orange-800 flex items-center gap-2 mb-3">
                    <Shield className="w-5 h-5" />
                    Change Resistance Summary
                  </h4>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="p-3 bg-white rounded-lg text-center">
                      <div className="text-2xl font-bold text-red-600">{aggregatedData.totalCriticalRisks}</div>
                      <div className="text-xs text-gray-600">Critical Risks</div>
                      <div className="text-xs text-red-600">Job displacement concerns</div>
                    </div>
                    <div className="p-3 bg-white rounded-lg text-center">
                      <div className="text-2xl font-bold text-orange-600">{aggregatedData.totalHighRisks}</div>
                      <div className="text-xs text-gray-600">High Risks</div>
                      <div className="text-xs text-orange-600">Political complexity</div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="bg-green-50 rounded-xl border border-green-200 p-4">
                  <h4 className="font-semibold text-green-800 flex items-center gap-2">
                    <CheckCircle className="w-5 h-5" />
                    Low Resistance Risk
                  </h4>
                  <p className="text-sm text-green-700 mt-2">
                    No critical job displacement or major political complexity risks detected across opportunities.
                  </p>
                </div>
              )}
            </div>
          </div>

          <div className="bg-indigo-100 rounded-xl p-4 border border-indigo-200">
            <h4 className="font-semibold text-indigo-800 flex items-center gap-2 mb-2">
              <Target className="w-5 h-5" />
              Executive Action Items
            </h4>
            <ul className="text-sm text-indigo-700 space-y-1">
              <li>• Schedule alignment meeting with {aggregatedData.bottleneckStakeholders.length > 0 ? 'bottleneck stakeholders' : 'key approvers'} to discuss prioritization</li>
              {aggregatedData.batchingSuggestions.length > 0 && (
                <li>• Implement batched approval process to reduce {aggregatedData.totalApprovals} individual approvals</li>
              )}
              {aggregatedData.totalCriticalRisks > 0 && (
                <li>• Develop change management plan addressing {aggregatedData.totalCriticalRisks} job impact concerns</li>
              )}
              <li>• Allocate {aggregatedData.totalEffortHours} hours of stakeholder time for implementation support</li>
            </ul>
          </div>
        </div>
      )}
    </div>
  );
};

export default StakeholderSummary;
